import { transport, useTransportQuery } from "@/transport"

import type { GetReviewDisagreementStateRequest } from "@/types_api/backend/transport/get_review_disagreement_state_request"
import type {
  GetReviewDisagreementStateResponse,
  ReviewDisagreementProjectStateResource,
} from "@/types_api/backend/transport/get_review_disagreement_state_response"

export interface ReviewDisagreementProjectState {
  projectId: string
  unresolvedSimulationCount: number
  unresolvedUserSimulationCount: number
  unresolvedSimulationIds: string[]
  unresolvedUserSimulationIds: string[]
  unresolvedAnnotationIds: string[]
}

interface ReviewDisagreementState {
  projects: ReviewDisagreementProjectState[]
  projectsById: Record<string, ReviewDisagreementProjectState>
}

const mapReviewDisagreementProjectState = (
  project: ReviewDisagreementProjectStateResource,
): ReviewDisagreementProjectState => ({
  projectId: project.project_id,
  unresolvedSimulationCount: project.unresolved_simulation_count ?? 0,
  unresolvedUserSimulationCount: project.unresolved_user_simulation_count ?? 0,
  unresolvedSimulationIds: project.unresolved_simulation_ids ?? [],
  unresolvedUserSimulationIds: project.unresolved_user_simulation_ids ?? [],
  unresolvedAnnotationIds: project.unresolved_annotation_ids ?? [],
})

export function mapReviewDisagreementStateResponse(
  response: GetReviewDisagreementStateResponse,
): ReviewDisagreementState {
  const projects = response.projects.map(mapReviewDisagreementProjectState)
  return {
    projects,
    projectsById: Object.fromEntries(
      projects.map((project) => [project.projectId, project]),
    ),
  }
}

export function useReviewDisagreementStateTransport({
  projectIds,
  selectedProjectId,
  includeUnresolvedIds = false,
  enabled = true,
}: {
  projectIds: string[]
  selectedProjectId?: string | null
  includeUnresolvedIds?: boolean
  enabled?: boolean
}) {
  const hasProjectScope = projectIds.length > 0
  const requestProjectIds: GetReviewDisagreementStateRequest["project_ids"] =
    hasProjectScope ? [projectIds[0], ...projectIds.slice(1)] : [""]

  const request: GetReviewDisagreementStateRequest = {
    project_ids: requestProjectIds,
    include_unresolved_ids: includeUnresolvedIds,
    ...(selectedProjectId !== undefined
      ? { selected_project_id: selectedProjectId }
      : {}),
  }

  return useTransportQuery({
    operation: transport.operations.getReviewDisagreementState,
    request,
    options: {
      enabled: hasProjectScope && enabled,
      select: mapReviewDisagreementStateResponse,
    },
  })
}
